import React, { useEffect } from 'react';

import { observer } from 'mobx-react';
import { FormProvider, useForm } from 'react-hook-form';

import classes from './SelectCoachStep.module.scss';
import { PageTitle } from '../../../../../shared/components/ui-components';
import { ButtonRow } from '../../../../components/customer-self-setup-components';
import { UseStepperReturn } from '../../../../../shared/hooks/useStepper';
import { coachStore } from '../../../../stores/customerSelfSetupStores/coachStore';
import { CollaborationTypeRow } from './CollaborationTypeRow/CollaborationTypeRow';
import { CoachListFilterFormValues } from '../../../../interfaces/coachList/CoachListFilters';
import { CoachTable } from './CoachTable/CoachTable';
import { useAutoSubmitForm } from '../../../../../shared/hooks/useAutoSubmitForm';
import { useScrollToTop } from '../../../../../shared/hooks/useScrollToTop';
import { useUpsertCustomer } from '../../../../hooks/customerSelfSetupHooks/useUpsertCustomer';
import { CONSULTATION_TYPE } from '../../../../../shared/constants/coach/consultationType';
import { personalInfoStore } from '../../../../stores/customerSelfSetupStores/personalInfoStore';

type SelectCoachStepProps = {
  stepperMethods: UseStepperReturn;
};

const SelectCoachStep = observer(({ stepperMethods }: SelectCoachStepProps) => {
  useScrollToTop();

  const { selectedCoach, isKnownCoach, filterMethods } = coachStore;

  const formMethods = useForm<CoachListFilterFormValues>({
    defaultValues: filterMethods.filterData,
  });

  const { handleSubmit, watch, reset } = formMethods;

  const { totalElements, defaultFilterData, applyFilters, resetFilterData } =
    filterMethods;

  const { upsertCustomer } = useUpsertCustomer();

  useAutoSubmitForm(handleSubmit(applyFilters), watch, ['coachName', 'entityName']);

  useEffect(() => {
    const isCoachNotSelected = !selectedCoach; // go from next step to select coach
    const newDefaultFilterData: CoachListFilterFormValues = {
      ...defaultFilterData,
      consultationType: CONSULTATION_TYPE.OFFLINE,
      zipCode: personalInfoStore.customerData?.zipCode || '',
      maxDistance: '',
    };

    if (isKnownCoach && isCoachNotSelected) {
      resetFilterData(newDefaultFilterData);
      reset(newDefaultFilterData);
    }
  }, []);

  const handleNextStep = async () => {
    await upsertCustomer();

    stepperMethods.goToNextStep();
  };

  const handlePreviousStep = () => {
    coachStore.resetSelectCoachList();
    stepperMethods.goToPreviousStep();
  };

  return (
    <FormProvider {...formMethods}>
      {!isKnownCoach && <CollaborationTypeRow />}
      <PageTitle
        className={classes.title}
        title={`Coach list (${totalElements || 0})`}
        customTopMargin
      />
      <CoachTable />
      <ButtonRow
        yesHandler={handleNextStep}
        yesDisabled={!selectedCoach}
        noHandler={handlePreviousStep}
      />
    </FormProvider>
  );
});

export { SelectCoachStep };
